import ProducerService from '@/services/ProducerService'
import { DataTableFilter } from '@/types/DataTable'
import Event from '@/types/Event'
import Producer from '@/types/Producer'
import { DateTime } from 'luxon'
import { computed, Ref, ref, watch } from 'vue'

interface EventFilterPayload {
  producer: Producer
  label: string
  field: string
  type: string
  initial?: string | null
  queryParams?: Record<string, string>
}

export function useEventFilter({
  producer,
  label,
  field,
  type,
  initial = null,
  queryParams,
}: EventFilterPayload): DataTableFilter<string, Event> {
  const loading = ref(false)

  const expires = ref<DateTime>()

  const model: Ref<string | null> = ref(initial)

  const items: Ref<Event[]> = ref([])

  const ready = Promise.withResolvers<boolean>()

  const load = async () => {
    loading.value = true

    const service = new ProducerService()

    const response = await service.events(1, new URLSearchParams(queryParams).toString(), 100)

    items.value = response.data.data

    if (model.value === null && items.value.length > 0) {
      model.value = items.value[0].id
    }

    expires.value = DateTime.now().plus({ minutes: 5 })

    ready.resolve(true)

    loading.value = false
  }

  watch(producer, () => load(), { immediate: true })

  return {
    model,
    value: computed(() => model.value),
    items: computed(
      () => {
        if (expires.value !== undefined && DateTime.now() > expires.value) {
          load()
        }

        return items.value.map(
          i => ({
            title: `${i.title} (${DateTime.fromSQL(i.dates.start).toFormat('dd/MM/yyyy HH:mm:ss')})`,
            value: i.id,
          }),
        )
      },
    ),
    resource: computed(() => items.value.find(item => item.id === model.value)),
    label,
    field,
    type,
    loading,
    ready: ready.promise,
    required: true,
  }
}
